import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { ar } from './locales/ar/index.js';
import { en, NAMESPACES } from './locales/en/index.js';
import { fr } from './locales/fr/index.js';
import { nl } from './locales/nl/index.js';

/**
 * i18n setup.
 *
 * Direction is a property of the locale, not a separate user setting: the
 * document's `dir` follows whichever language is active, and the layout
 * mirrors through logical CSS properties alone. See docs/i18n.md.
 */

export const LOCALES = ['en', 'ar', 'nl', 'fr'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Shown in the sidebar switch; the endonym, so a reader recognises their own. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'EN',
  ar: 'ع',
  nl: 'NL',
  fr: 'FR',
};

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  nl: 'Nederlands',
  fr: 'Français',
};

const RTL_LOCALES: ReadonlySet<Locale> = new Set<Locale>(['ar']);

/**
 * Whether a locale is offered for selection.
 *
 * The design lists all four but disables Dutch and French, because the
 * prototype only composes English and Arabic. This implementation has
 * complete Dutch and French translations, so they are enabled — disabling
 * them would hide working translations behind a limitation of the mock.
 *
 * Set one to `false` to grey it out in the switcher without removing it.
 */
export const LOCALE_READY: Record<Locale, boolean> = {
  en: true,
  ar: true,
  nl: true,
  fr: true,
};

export function directionFor(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * Writes the active locale onto the document root.
 *
 * Called once before the first render and again whenever the locale changes.
 * Doing it only from a React effect would leave the first paint using the
 * `dir="ltr"` baked into index.html, so an Arabic visitor sees the layout
 * mirror after it is already on screen.
 */
export function applyDocumentLocale(locale: Locale, root: HTMLElement): void {
  root.lang = locale;
  root.dir = directionFor(locale);
}

/**
 * i18next resolves `en-GB` to `en` on its own, but the detector may report a
 * language we do not ship at all; fall back rather than render keys.
 */
export function normaliseLocale(value: string | undefined): Locale {
  if (!value) return DEFAULT_LOCALE;
  const base = value.split('-')[0]?.toLowerCase() ?? '';
  return isLocale(base) ? base : DEFAULT_LOCALE;
}

export const STORAGE_KEY = 'aem-control-room-locale';

const resources = { en, ar, nl, fr } as const;

export async function initI18n(): Promise<typeof i18n> {
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...LOCALES],
      // Without this, `en-GB` is treated as its own language and every lookup
      // misses before falling back.
      load: 'languageOnly',
      ns: [...NAMESPACES],
      defaultNS: 'common',
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: STORAGE_KEY,
        caches: ['localStorage'],
      },
      interpolation: {
        // React escapes output already; escaping twice mangles apostrophes.
        escapeValue: false,
      },
      returnNull: false,
    });

  return i18n;
}

export { i18n };
